import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel } from "../admin/fields";
import { Checkbox } from "../../components/ui/checkbox";

const UrlRow = ({ label, url, testid }) => (
  <div className="flex flex-col gap-1">
    <span className="field-label">{label}</span>
    <div className="flex min-w-0 items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-lg bg-linen px-3 py-2 font-mono text-xs text-ink/80" data-testid={testid}>{url}</code>
      <button type="button" className="btn-ghost shrink-0" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Copied"))} data-testid={`${testid}-copy`}><Copy className="h-4 w-4" /></button>
    </div>
  </div>
);

export const GooglePanel = () => {
  const { data } = useQuery({ queryKey: ["platform-google"], queryFn: () => api.get("/platform/google").then((r) => r.data) });
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setForm({ partner_id: data.partner_id, sites: Object.fromEntries(data.sites.map((s) => [s.slug, { enabled: s.enabled, country: s.country }])) });
  }, [data]);
  const save = async (new_token = false) => {
    if (new_token && !window.confirm("Create a new secret price link? You must then update it in Google Hotel Center.")) return;
    setBusy(true);
    try {
      qc.setQueryData(["platform-google"], (await api.put("/platform/google", { ...form, new_token })).data);
      toast.success("Google Hotels settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!form) return null;
  const setSite = (slug, p) => setForm((f) => ({ ...f, sites: { ...f.sites, [slug]: { ...f.sites[slug], ...p } } }));
  return (
    <Panel title="Google Hotels (free booking links)" hint="Lists your hotels on Google with a live price and a 'Book on official site' link. Give Google the three links below when you apply for or set up your Hotel Center account.">
      <div className="grid gap-4">
        <UrlRow label="Live price link (keep private)" url={data.urls.query} testid="google-query-url" />
        <UrlRow label="Hotel list feed" url={data.urls.hotel_list} testid="google-hotel-list-url" />
        <UrlRow label="Landing page file (Point of Sale)" url={data.urls.pos} testid="google-pos-url" />
        <UrlRow label="Paid ads: Final URL suffix in Google Ads (so paid clicks are counted separately)" url={data.ads_suffix} testid="google-ads-suffix" />
      </div>
      <label className="field mt-5 max-w-sm"><span className="field-label">Hotel Center account ID (optional)</span>
        <input className="field-input" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} data-testid="google-partner-id" />
      </label>
      <p className="field-label mb-2 mt-6">Sites listed on Google</p>
      <div className="divide-y divide-ink/5 rounded-xl border border-ink/10">
        {data.sites.map((s) => (
          <div key={s.slug} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`google-site-${s.slug}`}>
            <Checkbox checked={!!form.sites[s.slug]?.enabled} onCheckedChange={(v) => setSite(s.slug, { enabled: !!v })} data-testid={`google-site-${s.slug}-enabled`} />
            <span className="min-w-0 flex-1 break-words text-ink">{s.name} <span className="font-mono text-xs text-ink/45">/{s.slug}</span>
              <span className={`ml-2 text-xs ${s.ads.enabled ? "text-brand" : "text-ink/40"}`} data-testid={`google-site-${s.slug}-ads`}>
                {s.ads.enabled ? `Paid ads requested: ${s.currency} ${Number(s.ads.monthly_budget).toLocaleString()}/month${s.ads.note ? ` · ${s.ads.note}` : ""}` : "No paid ads"}
              </span>
            </span>
            <label className="flex items-center gap-2 text-xs text-ink/60">Country
              <input className="field-input !h-8 w-16 uppercase" maxLength={2} value={form.sites[s.slug]?.country || "KE"}
                onChange={(e) => setSite(s.slug, { country: e.target.value.toUpperCase() })} data-testid={`google-site-${s.slug}-country`} />
            </label>
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" className="btn-primary" disabled={busy} onClick={() => save(false)} data-testid="google-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
        <button type="button" className="btn-ghost" disabled={busy} onClick={() => save(true)} data-testid="google-new-token"><RefreshCw className="h-4 w-4" />New secret price link</button>
      </div>
    </Panel>
  );
};
